// react-bits · FadeContent（IntersectionObserver 变体，vendored — reactbits.dev）
// 说明：reactbits 现行 FadeContent 依赖 GSAP+ScrollTrigger；本项目为保持 CRA 构建轻量
// 不引 GSAP，采用 reactbits 早期的 IntersectionObserver 实现（同名、同 props，零依赖）。
// 元素滚动进入视口时淡入 + 轻微上移；可选 blur、delay（做卡片错峰）。
import { useRef, useEffect, useState } from 'react';

const FadeContent = ({
  children,
  blur = false,
  duration = 700,
  easing = 'ease-out',
  delay = 0,
  threshold = 0.12,
  initialOpacity = 0,
  className = '',
  style,
  ...rest
}) => {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;

    // 优雅降级：不支持 IntersectionObserver 时直接显示
    if (typeof IntersectionObserver === 'undefined') {
      setInView(true);
      return undefined;
    }

    let timer;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          observer.unobserve(el);
          timer = setTimeout(() => setInView(true), delay);
        }
      },
      { threshold }
    );
    observer.observe(el);

    return () => {
      observer.disconnect();
      if (timer) clearTimeout(timer);
    };
  }, [threshold, delay]);

  // prefers-reduced-motion：命中则跳过入场动画，直接最终态（CSS 媒体查询覆盖不了内联 transition）
  const prefersReduced =
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const animatedStyle = {
    opacity: inView ? 1 : initialOpacity,
    transform: inView ? 'translateY(0)' : 'translateY(16px)',
    filter: blur ? (inView ? 'blur(0px)' : 'blur(8px)') : 'none',
    transition: `opacity ${duration}ms ${easing}, transform ${duration}ms ${easing}, filter ${duration}ms ${easing}`,
    willChange: 'opacity, transform',
  };
  const staticStyle = { opacity: 1, transform: 'none', filter: 'none', transition: 'none' };

  return (
    <div
      ref={ref}
      className={className}
      style={{ ...(prefersReduced ? staticStyle : animatedStyle), ...style }}
      {...rest}
    >
      {children}
    </div>
  );
};

export default FadeContent;
